'use client';
import { useMemo, useState, Fragment } from 'react';
import {
  Box,
  Card,
  Stack,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  IconButton,
  TablePagination,
  Chip,
  LinearProgress
} from '@mui/material';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import TimelineIcon from '@mui/icons-material/Timeline';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import dayjs from 'dayjs';
import { useLanguage } from '../../../../utils/i18n';

const formatDateTime = (isoString) => {
  if (!isoString) return '-';
  return dayjs(isoString).format('DD MMM YYYY, HH:mm');
};
const formatHoursMinutes = (totalSeconds, t) => {
  if (!totalSeconds || totalSeconds < 0) return `0 ${t('time.hours', 'Hours')} 0 ${t('time.minutes', 'Minutes')}`;

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);

  return `${hours} ${t('time.hours', 'Hours')} ${minutes} ${t('time.minutes', 'Minutes')}`;
};
// Komponen Baris Tabel Expandable
const ExpandableRow = ({ row, index }) => {
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const { t } = useLanguage();

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedTrips = row.trips.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  return (
    <Fragment>
      {/* Baris Utama (Summary Kendaraan) */}
      <TableRow
        sx={{
          '& > *': { borderBottom: 'unset' },
          bgcolor: open ? 'rgba(72, 93, 154, 0.04)' : 'transparent',
          transition: 'background-color 0.3s'
        }}
      >
        <TableCell padding="checkbox">
          <IconButton size="small" onClick={() => setOpen(!open)} sx={{ color: open ? '#485D9A' : 'text.secondary' }}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell align="center">{index}</TableCell>
        <TableCell sx={{ fontWeight: 600 }}>{row.vehicleNumber}</TableCell>
        <TableCell align="center">{row.retase}</TableCell>
        <TableCell align="center">{row.avgRetase}</TableCell>
        <TableCell align="center">{row.totalHours}</TableCell>
        <TableCell align="center">{row.hoursPerDay}</TableCell>
      </TableRow>

      {/* Baris Expand (Detail Trip & Routes) */}
      <TableRow>
        <TableCell
          style={{ paddingBottom: 0, paddingTop: 0, borderBottom: open ? '1px solid rgba(224, 224, 224, 1)' : 'none' }}
          colSpan={7}
        >
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ m: 2, p: 2, bgcolor: '#ffffff', borderRadius: 2, border: '1px dashed #cfd8dc' }}>
              <Typography variant="subtitle2" fontWeight="700" color="#485D9A" gutterBottom display="flex" alignItems="center" gap={1}>
                <TimelineIcon fontSize="small" /> {t('achievement.tripDetail', 'Trip Details')}
              </Typography>

              <Table size="small" sx={{ mt: 1, '& .MuiTableCell-root': { borderColor: '#eceff1' } }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#485D9A' }}>
                    <TableCell align="center" sx={{ fontWeight: 600, color: '#ffffff' }}>
                      No
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#ffffff' }}>{t('achievement.startTime', 'Start Time')}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#ffffff' }}>{t('achievement.endTime', 'End Time')}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#ffffff' }}>{t('achievement.duration', 'Duration')}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#ffffff' }}>{t('achievement.routes', 'Routes')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedTrips.map((trip, idx) => (
                    <TableRow key={idx} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell align="center" sx={{ fontSize: '0.8rem' }}>
                        {page * rowsPerPage + idx + 1}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{formatDateTime(trip.in_time)}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{formatDateTime(trip.out_time)}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{formatHoursMinutes(trip.duration, t)}</TableCell>
                      <TableCell>
                        {trip.visits && trip.visits.length > 0 ? (
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ py: 0.5 }}>
                            {trip.visits.map((visit, vIdx) => (
                              <Chip
                                key={vIdx}
                                size="medium"
                                label={`${visit.sequence_number || vIdx + 1}. ${visit.spatial_name}`}
                                sx={{
                                  fontSize: '0.7rem',
                                  bgcolor: '#fff',
                                  border: '1px solid #d0d7de',
                                  color: '#334155',
                                  fontWeight: 500
                                }}
                              />
                            ))}
                          </Stack>
                        ) : (
                          <Typography variant="caption" color="text.secondary" fontStyle="italic">
                            {t('achievement.noRoutes', 'No routes available')}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {row.trips.length > 0 && (
                <TablePagination
                  component="div"
                  count={row.trips.length}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                />
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </Fragment>
  );
};

export default function Achievement({ data = [], loading = false, loadingProgress = 0, loadingText = '' }) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const { t } = useLanguage();

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const processedData = useMemo(() => {
    if (!data || data.length === 0) return [];

    const grouped = {};

    data.forEach((trip) => {
      const vId = trip.vehicle_id;
      const plate = trip.license_plate || trip.vehicle_name || `Unknown-${vId}`;

      if (!grouped[vId]) {
        grouped[vId] = {
          id: vId,
          vehicleNumber: plate,
          retase: 0,
          totalSeconds: 0,
          activeDays: new Set(),
          trips: [] // Simpan array trip untuk di-render di tabel expanded
        };
      }

      grouped[vId].retase += trip.ritase_count || trip.details?.length || 1;
      grouped[vId].trips.push(trip);

      if (trip.in_time) {
        grouped[vId].activeDays.add(dayjs(trip.in_time).format('YYYY-MM-DD'));
      }

      let durationSec = trip.duration || 0;
      if (durationSec === 0 && trip.in_time && trip.out_time) {
        const start = dayjs(trip.in_time);
        const end = dayjs(trip.out_time);
        durationSec = end.diff(start, 'second');
      }

      if (durationSec > 0) {
        grouped[vId].totalSeconds += durationSec;
      }
    });

    return Object.values(grouped).map((v, index) => {
      const daysCount = v.activeDays.size > 0 ? v.activeDays.size : 1;
      const formattedTotal = formatHoursMinutes(v.totalSeconds, t);
      const avgSecondsPerDay = v.totalSeconds / daysCount;
      const formattedAvgPerDay = formatHoursMinutes(avgSecondsPerDay, t);

      return {
        id: v.id || `achv-${index}`,
        vehicleNumber: v.vehicleNumber,
        retase: v.retase,
        avgRetase: (v.retase / daysCount).toFixed(1),
        totalHours: formattedTotal,
        hoursPerDay: formattedAvgPerDay,
        trips: v.trips
      };
    });
  }, [data, t]);

  const paginatedData = processedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Card sx={{ p: 2, borderRadius: 2, boxShadow: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              bgcolor: '#485D9A',
              color: 'white',
              borderRadius: 2,
              width: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <VerifiedUserIcon />
          </Box>
          <Typography variant="h4" fontWeight="bold">
            {t('achievement.title', 'Achievement')}
          </Typography>
        </Stack>
        <Button variant="outlined" size="small" sx={{ borderRadius: 2, textTransform: 'none', borderColor: '#485D9A', color: '#485D9A' }}>
          {t('general.viewAll', 'View All')}
        </Button>
      </Stack>

      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
        <Table aria-label="achievement expandable table">
          <TableHead>
            <TableRow sx={{ bgcolor: '#485D9A' }}>
              <TableCell />
              <TableCell align="center" sx={{ fontWeight: 'bold', color: '#ffffff' }}>
                No
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#ffffff' }}>{t('achievement.vehicleNumber', 'Vehicle Number')}</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', color: '#ffffff' }}>
                {t('achievement.ritase', 'Ritase')}
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', color: '#ffffff' }}>
                {t('achievement.avgRitase', 'Average Ritase/Day')}
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', color: '#ffffff' }}>
                {t('achievement.totalWorkingHours', 'Total Working Hours')}
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', color: '#ffffff' }}>
                {t('achievement.workingHoursDay', 'Working Hours/Day')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                  {loadingProgress > 0 ? (
                    <Box sx={{ width: '60%', mx: 'auto', mt: 2, mb: 2 }}>
                      <LinearProgress
                        variant="determinate"
                        value={loadingProgress}
                        sx={{
                          height: 10,
                          borderRadius: 5,
                          mb: 1.5,
                          bgcolor: 'rgba(72, 93, 154, 0.2)',
                          '& .MuiLinearProgress-bar': { bgcolor: '#485D9A' }
                        }}
                      />
                      <Typography variant="body2" color="text.secondary" fontWeight="bold">
                        {loadingProgress}% - {loadingText}
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <DirectionsCarIcon
                        sx={{
                          color: '#485D9A',
                          fontSize: 48,
                          animation: 'bounce 0.6s infinite alternate',
                          '@keyframes bounce': {
                            '0%': { transform: 'translateY(0)' },
                            '100%': { transform: 'translateY(-12px)' }
                          },
                          mb: 1
                        }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {loadingText || t('general.calculating', 'Calculating achievement data...')}
                      </Typography>
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            ) : paginatedData.length > 0 ? (
              paginatedData.map((row, index) => <ExpandableRow key={row.id} row={row} index={page * rowsPerPage + index + 1} />)
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 0.5 }}>
                  {t('general.noData', 'No data available for the selected time range.')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={processedData.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Card>
  );
}
